import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { GroupBreadcrumbs } from "@dashboard/files/components/GroupBreadcrumbs";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { getParentGroups } from "@dashboard/groups/utils";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { FormControlLabel, Theme, Typography } from "@mui/material";
import { StyledCheckbox } from "@shared/form/CheckboxField";

const useStyles = makeStyles()((theme: Theme) => ({
    breadcrumbsRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    breadcrumbsLeft: {
        flex: 1,
        minWidth: 0,
    },
    includeParentControl: {
        marginLeft: 0,
        marginRight: 0,
        flexShrink: 0,
    },
    includeParentLabel: {
        color: theme.palette.emteria.text.primary,
    },
}));

type Props = {
    selectedGroupId: number | null;
    includeParentFiles: boolean;
    onIncludeParentFilesChange: (value: boolean) => void;
    onSelect?: (groupId: number | null) => void;
};

export const FileExplorerHeader = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels", "common"]);
    const groups = useSelector(selectGroups);
    const { data: workspace } = useCurrentWorkspace();

    const breadcrumbGroups = getParentGroups(props.selectedGroupId, groups).reverse();

    return (
        <div className={classes.breadcrumbsRow}>
            <div className={classes.breadcrumbsLeft}>
                <GroupBreadcrumbs
                    groups={breadcrumbGroups}
                    workspaceLabel={workspace?.name?.trim() || t("common:label.workspace")}
                    onSelect={props.onSelect}
                />
            </div>
            {props.selectedGroupId !== null && (
                <FormControlLabel
                    className={classes.includeParentControl}
                    control={
                        <StyledCheckbox
                            size="small"
                            checked={props.includeParentFiles}
                            onChange={(e) => props.onIncludeParentFilesChange(e.target.checked)}
                        />
                    }
                    label={
                        <Typography variant="body2" component="span" noWrap className={classes.includeParentLabel}>
                            {t("sidepanels:fileExplorer.includeParentFiles")}
                        </Typography>
                    }
                />
            )}
        </div>
    );
};
